import { SimpleObservable, SimpleObservableSubject } from "./observable";
import type { Logger } from "./logger";
import type { TrackEventPayload } from "@/services/loyalty/types";

export type SDKTrackerTrackEvent = {
  element: HTMLElement;
  actionIdOrAlias: string;
  action: string;
  payload: TrackEventPayload;
};

export type TrackedElement = {
  element: HTMLElement;
  actionIdOrAlias: string;
  eventListener: (event: Event) => void;
};

enum TrackDataAttribute {
  TRACK_ACTION = "data-fanfest-track",
  TRACK_ON = "data-fanfest-on",
  TRACK_DESCRIPTION = "data-fanfest-description",
  TRACK_EXPERIENCE_ID = "data-fanfest-experience-id",
  TRACK_OBJECT_ID = "data-fanfest-object-id",
  TRACK_META = "data-fanfest-meta-",
}

enum TrackEventName {
  click = "click",
  render = "render",
  submit = "submit",
}

enum TrackEventModifier {
  stop = "stop",
  capture = "capture",
  preventDefault = "preventDefault",
  once = "once",
}

const DEFAULT_MODIFIERS: Record<TrackEventModifier, boolean> = {
  [TrackEventModifier.stop]: false,
  [TrackEventModifier.capture]: false,
  [TrackEventModifier.preventDefault]: false,
  [TrackEventModifier.once]: false,
};

const DEFAULT_EVENT_NAME = TrackEventName.click;

function getTrackEventForElement(
  element: HTMLElement,
  logger: Logger,
): {
  eventName: TrackEventName;
  modifiers: Record<TrackEventModifier, boolean>;
} {
  const onAttribute = element.getAttribute(TrackDataAttribute.TRACK_ON);

  if (!onAttribute) {
    return {
      eventName: DEFAULT_EVENT_NAME,
      modifiers: DEFAULT_MODIFIERS,
    };
  }

  const [event, ...modifiers] = onAttribute.split(":");

  return {
    eventName: event as TrackEventName,
    modifiers: modifiers.reduce((acc, modifier) => {
      if (
        Object.values(TrackEventModifier).includes(
          modifier as TrackEventModifier,
        )
      ) {
        acc[modifier as TrackEventModifier] = true;
      } else {
        logger.warn(`Unknown track event modifier: ${modifier}`);
      }

      return acc;
    }, {} as Record<TrackEventModifier, boolean>),
  };
}

export class SDKTracker extends SimpleObservable {
  #trackedElements: Map<HTMLElement, TrackedElement> = new Map();
  #observer: MutationObserver | null = null;
  #logger: Logger;
  #isInitialized = false;

  onTrackEvent = new SimpleObservableSubject<SDKTrackerTrackEvent>();

  constructor(logger: Logger) {
    super();
    this.#logger = logger.extend(this.constructor.name);

    this.onTrackEvent.subscribe((event) => {
      this.#logger.info("Track event triggered", event);
    });
  }

  init() {
    if (this.#isInitialized) {
      this.#logger.warn("Tracker already initialized");
      return;
    }

    this.#isInitialized = true;

    // Initial setup of existing elements
    this.#setupExistingElements();

    // Setup MutationObserver to watch for DOM changes
    this.#setupMutationObserver();

    this.#logger.debug("Tracker initialized");
  }

  destroy() {
    if (!this.#isInitialized) {
      return;
    }

    // Disconnect the observer
    if (this.#observer) {
      this.#observer.disconnect();
      this.#observer = null;
    }

    // Remove all event listeners
    this.#removeAllEventListeners();

    // Clear tracked elements
    this.#trackedElements.clear();

    this.#isInitialized = false;
    this.#logger.debug("Tracker destroyed");
  }

  #setupExistingElements() {
    // Find all existing elements with data-fanfest-track attribute
    const elements = document.querySelectorAll(
      "[data-fanfest-track]",
    ) as NodeListOf<HTMLElement>;

    elements.forEach((element) => {
      this.#setupElement(element);
    });
  }

  #setupMutationObserver() {
    this.#observer = new MutationObserver((mutations) => {
      let shouldReinitialize = false;

      mutations.forEach((mutation) => {
        // Check for added nodes
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as HTMLElement;

            // Check if the added element has the track attribute
            if (element.hasAttribute("data-fanfest-track")) {
              this.#setupElement(element);
              shouldReinitialize = true;
            }

            // Check if any child elements have the track attribute
            const childElements = element.querySelectorAll(
              "[data-fanfest-track]",
            ) as NodeListOf<HTMLElement>;
            childElements.forEach((childElement) => {
              this.#setupElement(childElement);
              shouldReinitialize = true;
            });
          }
        });

        // Check for removed nodes
        mutation.removedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as HTMLElement;

            // Remove the element if it was tracked
            if (this.#trackedElements.has(element)) {
              this.#removeElement(element);
            }

            // Check if any child elements were tracked
            const childElements = element.querySelectorAll(
              "[data-fanfest-track]",
            ) as NodeListOf<HTMLElement>;
            childElements.forEach((childElement) => {
              if (this.#trackedElements.has(childElement)) {
                this.#removeElement(childElement);
              }
            });
          }
        });

        // Check for attribute changes
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "data-fanfest-track"
        ) {
          const element = mutation.target as HTMLElement;

          if (element.hasAttribute("data-fanfest-track")) {
            // Element now has the attribute, set it up
            this.#setupElement(element);
          } else {
            // Element no longer has the attribute, remove it
            this.#removeElement(element);
          }
          shouldReinitialize = true;
        }
      });

      if (shouldReinitialize) {
        this.#logger.info(
          "DOM changes detected, reinitializing tracked elements",
        );
      }
    });

    // Start observing
    this.#observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["data-fanfest-track"],
    });
  }

  #setupElement(element: HTMLElement) {
    // Skip if already tracked
    if (this.#trackedElements.has(element)) {
      return;
    }

    const actionIdOrAlias = element.getAttribute("data-fanfest-track");
    if (!actionIdOrAlias) {
      return;
    }

    const { eventName, modifiers } = getTrackEventForElement(
      element,
      this.#logger,
    );

    if (eventName === TrackEventName.render) {
      this.#handleTrackEvent(element, actionIdOrAlias);
      return;
    }

    // Create event listener
    const eventListener = (event: Event) => {
      if (modifiers[TrackEventModifier.stop]) {
        event.stopPropagation();
      }

      if (modifiers[TrackEventModifier.preventDefault]) {
        event.preventDefault();
      }

      this.#handleTrackEvent(element, actionIdOrAlias);
    };

    // Add event listener to the element
    element.addEventListener(eventName, eventListener, {
      capture: modifiers[TrackEventModifier.capture],
      passive: !modifiers[TrackEventModifier.preventDefault],
      once: modifiers[TrackEventModifier.once],
    });

    // Store the tracked element
    this.#trackedElements.set(element, {
      element,
      actionIdOrAlias,
      eventListener,
    });

    this.#logger.debug("Tracking element", {
      element: element.tagName,
      actionIdOrAlias,
      totalTracked: this.#trackedElements.size,
    });
  }

  #removeElement(element: HTMLElement) {
    const trackedElement = this.#trackedElements.get(element);
    if (!trackedElement) {
      return;
    }

    for (const eventName of Object.values(TrackEventName)) {
      // Remove event listener
      element.removeEventListener(eventName, trackedElement.eventListener);
    }

    // Remove from tracked elements
    this.#trackedElements.delete(element);

    this.#logger.debug("Removed tracking from element", {
      element: element.tagName,
      actionIdOrAlias: trackedElement.actionIdOrAlias,
      totalTracked: this.#trackedElements.size,
    });
  }

  #removeAllEventListeners() {
    this.#trackedElements.forEach((trackedElement) => {
      for (const eventName of Object.values(TrackEventName)) {
        trackedElement.element.removeEventListener(
          eventName,
          trackedElement.eventListener,
        );
      }
    });
  }

  async #handleTrackEvent(element: HTMLElement, actionIdOrAlias: string) {
    try {
      // Get additional data from element attributes
      const description = element.getAttribute(
        TrackDataAttribute.TRACK_DESCRIPTION,
      );
      const externalExperienceId = element.getAttribute(
        TrackDataAttribute.TRACK_EXPERIENCE_ID,
      );
      const externalObjectId = element.getAttribute(
        TrackDataAttribute.TRACK_OBJECT_ID,
      );

      // Get metadata from data attributes
      const metadata: Record<string, string | number | boolean> = {};
      const dataAttributes = element.attributes;
      for (let i = 0; i < dataAttributes.length; i++) {
        const attr = dataAttributes[i]!;

        if (attr.name.startsWith(TrackDataAttribute.TRACK_META)) {
          const key = attr.name.replace(TrackDataAttribute.TRACK_META, "");
          metadata[key] = attr.value;
        }
      }

      // Create track event payload
      const trackEvent: SDKTrackerTrackEvent = {
        element,
        actionIdOrAlias,
        action: actionIdOrAlias,
        payload: {
          action: actionIdOrAlias,
          description: description || undefined,
          externalExperienceId: externalExperienceId || undefined,
          externalObjectId: externalObjectId || undefined,
          metadata: metadata || undefined,
        },
      };

      this.notify(this.onTrackEvent, trackEvent);
    } catch (error) {
      this.#logger.error("Error handling track event", { error });
    }
  }

  // Public method to manually track an element
  trackElement(element: HTMLElement, actionIdOrAlias: string) {
    if (!this.#isInitialized) {
      this.#logger.warn("Tracker not initialized");
      return;
    }

    element.setAttribute("data-fanfest-track", actionIdOrAlias);
    this.#setupElement(element);
  }

  // Public method to stop tracking an element
  untrackElement(element: HTMLElement) {
    if (!this.#isInitialized) {
      this.#logger.warn("Tracker not initialized");
      return;
    }

    element.removeAttribute("data-fanfest-track");
    this.#removeElement(element);
  }

  // Get statistics about tracked elements
  getStats() {
    return {
      totalTracked: this.#trackedElements.size,
      isInitialized: this.#isInitialized,
    };
  }
}
